import axios from 'axios';
import type {
  CharityDonationsPage,
  CharityYearDetail,
  DonationYearSummary,
  DonorTaxProfile,
  ParticipatingMerchant,
  ParticipatingMerchants,
  SaveIcNumberPayload
} from '../types/donation.types';
import { apiClient } from './client';

/** The donor's Giving data, from the user-portal backend's `donations` routes. */

interface Envelope<T> {
  success: true;
  status: number;
  data: T;
}

const segment = encodeURIComponent;

/** The backend's `error.code` on a failed JSON call; null when there is none. */
export const apiErrorCode = (error: unknown): string | null => {
  if (!axios.isAxiosError(error)) return null;
  const code: unknown = error.response?.data?.error?.code;
  return typeof code === 'string' ? code : null;
};

/** Years with at least one paid donation, newest first. */
export const getDonationYears = async (): Promise<number[]> => {
  const response = await apiClient.get<Envelope<number[]>>('/donations/years');
  return response.data.data;
};

export const getDonationYearSummary = async (
  year: number
): Promise<DonationYearSummary> => {
  const response = await apiClient.get<Envelope<DonationYearSummary>>(
    `/donations/years/${year}`
  );
  return response.data.data;
};

export const getCharityYearDetail = async (
  charityId: string,
  year: number
): Promise<CharityYearDetail> => {
  const response = await apiClient.get<Envelope<CharityYearDetail>>(
    `/donations/charities/${segment(charityId)}`,
    { params: { year } }
  );
  return response.data.data;
};

/** One page of a charity's donations in a year, newest first; `search` matches store or receipt no. */
export const getCharityDonations = async (
  charityId: string,
  year: number,
  page: number,
  search: string
): Promise<CharityDonationsPage> => {
  const term = search.trim();
  const response = await apiClient.get<Envelope<CharityDonationsPage>>(
    `/donations/charities/${segment(charityId)}/donations`,
    { params: { year, page, ...(term ? { search: term } : {}) } }
  );
  return response.data.data;
};

/** Up to five merchants in the donor's market whose checkout takes donations. */
export const getParticipatingMerchants = async (): Promise<
  ParticipatingMerchant[]
> => {
  const response = await apiClient.get<Envelope<ParticipatingMerchants>>(
    '/donations/participating-merchants'
  );
  return response.data.data.merchants;
};

export const getDonorTaxProfile = async (): Promise<DonorTaxProfile> => {
  const response = await apiClient.get<Envelope<DonorTaxProfile>>(
    '/donations/tax-profile'
  );
  return response.data.data;
};

export const saveDonorIcNumber = async (
  payload: SaveIcNumberPayload
): Promise<DonorTaxProfile> => {
  const response = await apiClient.put<Envelope<DonorTaxProfile>>(
    '/donations/tax-identity',
    payload
  );
  return response.data.data;
};
